import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { ApiService } from 'app/core/api/api.service';
import { BehaviorSubject, Observable, tap, Subject, catchError, throwError } from 'rxjs';

@Injectable({
    providedIn: 'root',
})
export class ProjectService {
    private _data: BehaviorSubject<any> = new BehaviorSubject(null);
    private _apiService = inject(ApiService);
    private _updated = new Subject<Object>();
    updated$ = this._updated.asObservable();
    private dataStore: { companies: any[] } = { companies: [] };

    /**
     * Constructor
     */
    constructor(private _httpClient: HttpClient) {}

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Getter for data
     */
    get data$(): Observable<any> {
        return this._data.asObservable();
    }

    get(): any {
        return this.dataStore.companies;
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Get data
     */
    getData(): Observable<any> {
        return this._apiService.get('dashboards/project').pipe(
            tap((response: any) => {
                this._data.next(response);
            })
        );
    }

    /**
     * Get all company admins (replacing getAllCompaniesForSuperAdmin)
     */
    getAllCompanyAdmins(queryParams?: {
        sortBy?: string;
        limit?: number;
        page?: number;
    }): Observable<any> {
        // Build query parameters
        let queryString = '';
        if (queryParams) {
            const params = new URLSearchParams();
            if (queryParams.sortBy) params.append('sortBy', queryParams.sortBy);
            if (queryParams.limit) params.append('limit', queryParams.limit.toString());
            if (queryParams.page) params.append('page', queryParams.page.toString());

            queryString = params.toString() ? `?${params.toString()}` : '';
        }

        return this._apiService.get(`users/company-admins${queryString}`).pipe(
            tap((response: any) => {
                this._data.next(response);
                this._updated.next(response);
                // Store the company admins data instead of companies
                this.dataStore.companies = response.results || [];
            })
        );
    }

    /**
     * Create admin with company
     */
    createAdminWithCompany(data: {
        name: string;
        email: string;
        companyName: string;
    }): Observable<any> {
        return this._apiService.post('admin/create-admin-with-company', data);
    }

    resendCompanyAdminInvitation(adminId: string): Observable<any> {
        return this._apiService.post(`admin/company-admin-invitations/${adminId}/resend`, {});
    }

    revokeCompanyAdminInvitation(adminId: string): Observable<any> {
        return this._apiService.delete(`admin/company-admin-invitations/${adminId}`);
    }

    /**
     * Delete company admin
     */
    deleteCompanyAdmin(adminId: string): Observable<any> {
        return this._apiService.delete(`users/${adminId}`);
    }

    // In project.service.ts
    updateAdminBySuperAdmin(
        adminId: string,
        updateData: {
            name?: string;
            company?: string;
        }
    ): Observable<any> {
        const url = `/users/super-admin/admin/${adminId}`;

        return this._apiService.patch(url, updateData).pipe(
            tap((updatedAdmin: any) => {
                // Update the admin in the local data store
                if (this.dataStore.companies) {
                    const index = this.dataStore.companies.findIndex(
                        (admin) => admin._id === adminId
                    );
                    if (index !== -1) {
                        // Update the admin and company name
                        this.dataStore.companies[index] = {
                            ...this.dataStore.companies[index],
                            name: updatedAdmin.name,
                            email: updatedAdmin.email,
                            company: updatedAdmin.companyName
                                ? {
                                      ...this.dataStore.companies[index].company,
                                      name: updatedAdmin.companyName,
                                  }
                                : this.dataStore.companies[index].company,
                        };
                        this._data.next(this.dataStore.companies);
                        this._updated.next(this.dataStore.companies);
                    }
                }
            }),
            catchError((error: HttpErrorResponse) => {
                const errorMessage =
                    error.error?.message ||
                    error.error?.error?.message ||
                    error.message ||
                    'An error occurred while updating admin';
                return throwError(() => new Error(errorMessage));
            })
        );
    }
}
